// Segment names, order and colours for each model family, read from the
// tables that already define them, so a chart drawn for one family can be
// drawn for another without a second copy of the chart.
//
// The order is `WEIGHTS` and the fills are `CHART_COLORS`, both in utils; the
// fit labels are `SEGMENT_LABEL`. Nothing here defines a segment. Two things
// are decided here: an engagement or grade label as the product writes it,
// and the text on an engagement fill, which takes the colour measured for the
// fit tier at the same rank, so the two families read alike.
import { CHART_COLORS, WEIGHTS } from '../../../utils';
import { Threshold } from '../../../models/ModelPerformances';
import { SEGMENT_LABEL, segmentTextColor } from '../trees/nodeSegment';
import { TIERS } from '../trees/treePerformance';

export type Vocabulary = 'customer_fit' | 'behavioral' | 'lead_grade';

function bestFirst(weights: Record<string, number>): string[] {
  return Object.keys(weights).sort((a, b) => weights[b] - weights[a]);
}

/** Best first, the order every chart stacks them in. */
export const SEGMENTS: Record<Vocabulary, string[]> = {
  customer_fit: bestFirst(WEIGHTS.customer_fit),
  behavioral: bestFirst(WEIGHTS.behavioral),
  lead_grade: bestFirst(WEIGHTS.lead_grade),
};

const titleCase = (name: string) =>
  name.replace(/\b[a-z]/g, (letter) => letter.toUpperCase());

/** The segment name as the product writes it, in either vocabulary. */
export function segmentLabel(vocabulary: Vocabulary, segment: string): string {
  if (vocabulary === 'customer_fit') {
    return SEGMENT_LABEL[segment as Threshold] ?? segment;
  }
  if (vocabulary === 'lead_grade') return `Grade ${segment}`;
  return titleCase(segment);
}

/** The fill the performance charts use for that segment. */
export function segmentFill(
  vocabulary: Vocabulary,
  segment: string
): string | null {
  return CHART_COLORS[vocabulary]?.[segment]?.background ?? null;
}

/**
 * Text drawn ON that fill. The two vocabularies share the four hues position
 * for position, so the engagement tier borrows the contrast measured for the
 * fit tier at the same rank rather than claiming white on teal reads.
 */
export function segmentInk(
  vocabulary: Vocabulary,
  segment: string
): string | null {
  // The grade palette is its own ramp of one blue and carries a measured
  // text colour per step, so it is read rather than borrowed.
  if (vocabulary === 'lead_grade') {
    return CHART_COLORS.lead_grade?.[segment]?.text ?? null;
  }
  const rank = SEGMENTS[vocabulary].indexOf(segment);
  if (rank === -1) return null;
  return segmentTextColor(TIERS[rank]);
}

/** What the vocabulary counts: an account model scores accounts. */
export function unitOf(modelType: string): 'leads' | 'accounts' {
  return modelType === 'mqa' ? 'accounts' : 'leads';
}

export function vocabularyOf(modelType: string): Vocabulary {
  return modelType === 'customer_fit' ? 'customer_fit' : 'behavioral';
}
